import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createCredential, fetchPasswordByCredentialId, updateCredential } from "../services/api";
import { useCredentialsContext } from "../context/CredentialsContext";
import { calculateExpirationDate, formatExpirationDate } from "../utils/expiration";
import "../styles/credentialForm.css";

export default function CredentialForm({
    credential = null, setCredential, method = "view", editMode = false,
    setEditMode, successful = false, setSuccessful,
}) {
    const navigate = useNavigate();
    const { credentialID } = useParams();
    const { refresh } = useCredentialsContext();
    const isCreate = method === "create";
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [company, setCompany] = useState("");
    const [provider, setProvider] = useState("");
    const [duration, setDuration] = useState("until-complete");
    const [expiresAt, setExpiresAt] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [revealing, setRevealing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const hideTimeoutRef = useRef(null);
    const revealRequestRef = useRef(0);
    const savingRef = useRef(false);

    // Only reset when the record or mode changes, never when a draft field changes.
    useEffect(() => {
        setUsername(credential?.username ?? "");
        setCompany(credential?.company ?? "");
        setProvider(credential?.provider ?? "");
        setDuration(credential?.duration ?? "until-complete");
        setExpiresAt(credential?.expires_at ?? null);
        setPassword("");
        setIsPasswordVisible(false);
        setRevealing(false);
        setPasswordError(null);
        setSaveError(null);
        return () => {
            clearTimeout(hideTimeoutRef.current);
            revealRequestRef.current += 1;
        };
    }, [credential, method]);

    function hidePassword() {
        revealRequestRef.current += 1;
        clearTimeout(hideTimeoutRef.current);
        setPassword("");
        setIsPasswordVisible(false);
        setRevealing(false);
    }

    async function showPassword() {
        if (!credentialID || revealing) return;
        const request = ++revealRequestRef.current;
        setRevealing(true);
        setPasswordError(null);
        try {
            const value = await fetchPasswordByCredentialId(credentialID);
            if (request !== revealRequestRef.current) return;
            setPassword(value ?? "");
            setIsPasswordVisible(true);
            hideTimeoutRef.current = setTimeout(hidePassword, 5000);
        } catch {
            if (request === revealRequestRef.current) setPasswordError("Unable to load password. Please try again.");
        } finally {
            if (request === revealRequestRef.current) setRevealing(false);
        }
    }

    async function saveChanges(event) {
        event.preventDefault();
        if (!editMode || savingRef.current) return;
        setSaveError(null);
        if (!company.trim() || !provider.trim() || !username.trim() || (isCreate && !password)) {
            setSaveError("Enter a company, provider, username and password before saving.");
            return;
        }
        const payload = {
            company: company.trim(), provider: provider.trim(), username: username.trim(),
            // A blank replacement leaves the existing password unchanged.
            password: password || undefined,
            "login-successful": successful,
        };
        if (isCreate || duration !== (credential?.duration ?? "until-complete")) {
            const expiration = calculateExpirationDate(duration);
            payload.duration = duration;
            payload.expires_at = expiration ? expiration.toISOString() : null;
        }
        savingRef.current = true;
        setSaving(true);
        try {
            if (isCreate) {
                const created = await createCredential(payload);
                await refresh();
                navigate(created?.id ? `/credential/${created.id}` : "/");
            } else {
                const updated = await updateCredential(credentialID, payload);
                if (updated) {
                    setCredential(updated);
                    setSuccessful(updated["login-successful"]);
                }
                await refresh();
                setEditMode(false);
                hidePassword();
            }
        } catch {
            setSaveError("We couldn’t save this credential. Your changes are still here. Please try again.");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    }

    function cancelEdit() {
        const dirty = company !== (credential?.company ?? "") || provider !== (credential?.provider ?? "")
            || username !== (credential?.username ?? "") || password !== ""
            || duration !== (credential?.duration ?? "until-complete")
            || successful !== (credential?.["login-successful"] ?? true);
        if (dirty && !window.confirm("Discard your unsaved changes?")) return;
        if (isCreate) navigate("/");
        else {
            setSuccessful(credential["login-successful"]);
            setEditMode(false);
            hidePassword();
        }
    }

    return (
        <form id="credential-form" className={`credential-form mode-${method}`} onSubmit={saveChanges}>
            <fieldset className="form-inputs" disabled={saving}>
                <legend className="sr-only">Credential details</legend>
                {editMode && <>
                    <label htmlFor="credential-company">Company</label>
                    <input id="credential-company" name="company" value={company} required
                        autoComplete="off" onChange={(e) => setCompany(e.target.value)} />
                    <label htmlFor="credential-provider">Provider</label>
                    <input id="credential-provider" name="provider" value={provider} required
                        placeholder="e.g. CHAS or Constructionline" autoComplete="off"
                        onChange={(e) => setProvider(e.target.value)} />
                </>}
                {editMode ? <>
                    <label htmlFor="credential-username">Username</label>
                    <input id="credential-username" name="username" value={username}
                        required autoComplete="off" spellCheck={false} onChange={(e) => setUsername(e.target.value)} />
                </> : <>
                    <span className="credential-field-label">Username</span>
                    <p className="credential-readonly-value">{credential?.username ?? ""}</p>
                </>}
                {editMode
                    ? <label htmlFor="credential-password">{isCreate ? "Password" : "New password"}</label>
                    : <span className="credential-field-label">Password</span>}
                <div className="password-field">
                    {editMode
                        ? <input id="credential-password" name="password"
                            type={isPasswordVisible ? "text" : "password"}
                            value={password} required={isCreate} autoComplete="new-password" spellCheck={false}
                            aria-describedby={!isCreate ? "password-help" : undefined}
                            onChange={(e) => setPassword(e.target.value)} />
                        : <span className="credential-readonly-value" aria-label="Password">
                            {isPasswordVisible ? password : "••••••••"}
                        </span>}
                    <button type="button" className="password-toggle" disabled={revealing}
                        onClick={() => editMode ? setIsPasswordVisible(!isPasswordVisible)
                            : isPasswordVisible ? hidePassword() : showPassword()}>
                        {revealing ? "Loading…" : isPasswordVisible ? "Hide password" : "Show password"}
                    </button>
                    {editMode && !isCreate && <p id="password-help" className="field-help">Leave blank to keep the current password.</p>}
                    {!editMode && isPasswordVisible && <p className="field-help" role="status">Hidden automatically after 5 seconds.</p>}
                    {passwordError && <p className="error-message" role="alert">{passwordError}</p>}
                </div>
                <label htmlFor="credential-duration">Duration</label>
                {editMode ? <select id="credential-duration" name="duration" value={duration}
                    onChange={(e) => {
                        setDuration(e.target.value);
                        const expiry = calculateExpirationDate(e.target.value);
                        setExpiresAt(!isCreate && e.target.value === (credential?.duration ?? "until-complete")
                            ? credential?.expires_at ?? null : expiry ? expiry.toISOString() : null);
                    }}>
                    <option value="1-week">1 week</option>
                    <option value="30-days">30 days</option>
                    <option value="until-complete">Until complete</option>
                </select> : <p id="credential-duration" className="expiration-readonly">{duration.replaceAll("-", " ")}</p>}
                <span className="credential-field-label">Expires</span>
                <p className="expiration-readonly">{expiresAt ? formatExpirationDate(expiresAt) : "No expiration"}</p>
                <label htmlFor="credential-successful">Login confirmed</label>
                <div className="checkbox-field">
                    <input id="credential-successful" type="checkbox" name="successful" checked={successful}
                        disabled={!editMode} onChange={(e) => setSuccessful(e.target.checked)} />
                    <span className="field-help">{editMode ? "This login has been checked and works." : successful ? "Confirmed working" : "Needs checking — edit to update"}</span>
                </div>
            </fieldset>
            {saveError && <p className="error-message form-error" role="alert">{saveError}</p>}
            {editMode && <div className="credential-edit-actions">
                <button type="button" className="btn cancel-btn" disabled={saving} onClick={cancelEdit}>Cancel</button>
                <button type="submit" className="btn save-btn" disabled={saving}>
                    {saving ? "Saving…" : isCreate ? "Add credential" : "Save changes"}
                </button>
            </div>}
        </form>
    );
}
